import { useMemo } from 'react';

import { Heading, Icon, Stack, Text, cn } from '@eluve/components';
import {
  type GoalDiff,
  type PhaseDiff,
  type PhasedInstanceSpec,
  type TailorOrigin,
  diffInstanceSpec,
} from '@eluve/plan-shapes';

import { MarkdownProse } from './MarkdownProse';
import { formatCadence, formatDosage, formatDuration } from './format';

// The rendered care plan instance body, shared by the encounter Care Plan tab
// and the patient profile. The tab passes its living draft against the verbatim
// seed so the variance vs the template is COMPUTED per element (ADR-0013) —
// tailored/ad-hoc rows badged, skipped template content struck through,
// template-derived rows unmarked. The patient profile passes the stored
// instance as both seed and spec (zero diff → no variance chrome) for a clean
// read-only view. Pure render; the tab owns the chat + accept.
export const CarePlanInstancePreview: React.FC<{
  seed: PhasedInstanceSpec;
  spec: PhasedInstanceSpec;
  // The tab shows the plan title + start date above the body; the patient
  // profile renders its own header (status, formatted period) and hides this
  // one to avoid a duplicate title. Defaults to the tab's behavior.
  showHeader?: boolean;
}> = ({ seed, spec, showHeader = true }) => {
  const diff = useMemo(() => diffInstanceSpec(seed, spec), [seed, spec]);

  return (
    <Stack gap={4}>
      {showHeader ? (
        <div>
          <Stack direction="horizontal" align="center" gap={2}>
            <Heading size="lg" weight="semibold" level={3}>
              {spec.title}
            </Heading>
            {diff.titleChanged ? <ChangeBadge label="renamed" /> : null}
          </Stack>
          <Text
            size="xs"
            color="secondary"
            className="mt-1 flex items-center gap-1.5"
          >
            <Icon name="Calendar" size="auto" className="size-3.5" />
            Starts {spec.startDate} · v1.0 · Phased
            {diff.startDateChanged ? (
              <ChangeBadge label={`was ${seed.startDate}`} />
            ) : null}
          </Text>
        </div>
      ) : null}

      {spec.goals && spec.goals.length > 0 ? (
        <GoalsCallout
          spec={spec}
          goalDiffs={diff.goals}
          droppedGoals={diff.droppedGoals}
        />
      ) : null}

      <Stack gap={3}>
        {spec.phases.map((phase, i) => (
          <PhaseCard
            key={phase.code ?? i}
            phase={phase}
            index={i}
            phaseDiff={diff.phases[i]}
          />
        ))}
      </Stack>
    </Stack>
  );
};

function OriginBadge({
  origin,
  changedFields,
}: {
  origin: TailorOrigin;
  changedFields: string[];
}) {
  if (origin === 'template') {
    return null;
  }
  if (origin === 'tailored') {
    return (
      <span className="inline-flex items-center rounded-full bg-bg-warning-subtle px-2 py-0.5 text-[10px] font-medium text-text-warning">
        tailored
        {changedFields.length > 0 ? ` · ${changedFields.join(', ')}` : ''}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-bg-indigo-subtle-secondary px-2 py-0.5 text-[10px] font-medium text-text-indigo-subtle">
      ad-hoc
    </span>
  );
}

function ChangeBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-bg-warning-subtle px-2 py-0.5 align-middle text-[10px] font-medium text-text-warning">
      {label}
    </span>
  );
}

function GoalsCallout({
  spec,
  goalDiffs,
  droppedGoals,
}: {
  spec: PhasedInstanceSpec;
  goalDiffs: GoalDiff[];
  droppedGoals: string[];
}) {
  return (
    <div className="rounded-xl border border-border-positive-subtle bg-bg-positive-subtle px-4 py-3">
      <Text
        size="xs"
        weight="medium"
        className="tracking-wider text-text-positive uppercase"
      >
        Goal
      </Text>
      <ul className="mt-2 space-y-2">
        {(spec.goals ?? []).map((goal, i) => (
          <li key={i} className="text-sm text-foreground">
            <div className="flex flex-wrap items-center gap-2">
              <Icon
                name="Target"
                size="auto"
                className="size-3.5 shrink-0 text-text-positive"
              />
              <span>{goal.description}</span>
              {goalDiffs[i] ? (
                <OriginBadge
                  origin={goalDiffs[i].origin}
                  changedFields={goalDiffs[i].changedFields}
                />
              ) : null}
            </div>
          </li>
        ))}
        {droppedGoals.map((description) => (
          <li
            key={description}
            className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground"
          >
            <Icon
              name="Target"
              size="auto"
              className="size-3.5 shrink-0 opacity-50"
            />
            <span className="line-through">{description}</span>
            <span className="inline-flex items-center rounded-full bg-bg-tertiary px-2 py-0.5 text-[10px] font-medium text-text-secondary">
              dropped
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function PhaseCard({
  phase,
  index,
  phaseDiff,
}: {
  phase: PhasedInstanceSpec['phases'][number];
  index: number;
  phaseDiff: PhaseDiff | undefined;
}) {
  const skippedPhase = phase.durationDays === 0;
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-card px-4 py-3',
        skippedPhase && 'opacity-70',
      )}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="grid size-5 shrink-0 place-items-center rounded-full bg-muted font-mono text-[10px] text-foreground/70">
          {index + 1}
        </span>
        <span
          className={cn(
            'text-sm font-semibold text-foreground',
            skippedPhase && 'line-through',
          )}
        >
          {phase.title}
        </span>
        {phase.durationDays !== undefined && phase.durationDays > 0 ? (
          <span className="font-mono text-[11px] text-muted-foreground">
            {formatDuration(phase.durationDays)}
          </span>
        ) : skippedPhase ? (
          <span className="inline-flex items-center rounded-full bg-bg-tertiary px-2 py-0.5 text-[10px] font-medium text-text-secondary">
            skipped
          </span>
        ) : null}
        {phaseDiff?.durationChanged ? (
          <ChangeBadge label="duration shifted" />
        ) : null}
      </div>
      {phase.description ? (
        <MarkdownProse
          content={phase.description}
          className="mt-1 text-xs text-muted-foreground"
        />
      ) : null}

      {(phase.activities?.length ?? 0) > 0 ||
      (phaseDiff?.skipped.length ?? 0) > 0 ? (
        <ul className="mt-2 space-y-1.5">
          {(phase.activities ?? []).map((activity, i) => (
            <li
              key={i}
              className="rounded-lg border border-border/60 bg-background px-3 py-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Icon
                  name="Dumbbell"
                  size="auto"
                  className="size-3.5 shrink-0 text-muted-foreground"
                />
                <span className="text-sm font-medium text-foreground">
                  {activity.name}
                </span>
                {formatDosage(activity.dosage).map((part) => (
                  <span
                    key={part}
                    className="rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground/80"
                  >
                    {part}
                  </span>
                ))}
                {activity.frequency ? (
                  <span className="inline-flex items-center gap-1 rounded-sm bg-muted px-1.5 py-0.5 font-mono text-[11px] text-foreground/80">
                    <Icon name="CalendarClock" size="auto" className="size-3" />
                    {formatCadence(activity.frequency)}
                  </span>
                ) : null}
                <span className="ml-auto">
                  {phaseDiff?.activities[i] ? (
                    <OriginBadge
                      origin={phaseDiff.activities[i].origin}
                      changedFields={phaseDiff.activities[i].changedFields}
                    />
                  ) : null}
                </span>
              </div>
              {activity.patientInstructions ? (
                <p className="mt-1 pl-6 text-xs text-muted-foreground">
                  {activity.patientInstructions}
                </p>
              ) : null}
            </li>
          ))}
          {(phaseDiff?.skipped ?? []).map((name) => (
            <li
              key={name}
              className="flex items-center gap-2 rounded-lg border border-dashed border-border/60 px-3 py-2 text-sm text-muted-foreground"
            >
              <Icon
                name="Dumbbell"
                size="auto"
                className="size-3.5 shrink-0 opacity-50"
              />
              <span className="line-through">{name}</span>
              <span className="inline-flex items-center rounded-full bg-bg-tertiary px-2 py-0.5 text-[10px] font-medium text-text-secondary">
                skipped
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
